import { useState } from 'react';

import { ApiError } from '../../../../api/client';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { useToastStore } from '../../../../stores/toastStore';
import { useAddLayoutVariant, useLayoutVariants, useRemoveLayoutVariant } from '../hooks';
import type { DocumentRegionName, LayoutVariantResponse } from '../api';

const REGION_OPTIONS: ReadonlyArray<DocumentRegionName> = [
  'Header',
  'Footer',
  'BodyLeft',
  'BodyCenter',
  'BodyRight',
  'Table',
  'Signature',
  'Unknown',
];

interface LayoutVariantManagerProps {
  /** Document type slug; null while the editor is creating a new draft (no variants yet). */
  slug: string | null;
  /** Field names already declared on the parent type, used to build the region selector. */
  fieldNames: ReadonlyArray<string>;
}

/**
 * Embedded panel that lists the layout variants attached to a document type and lets the operator
 * register a new one from an exemplar job id. Hidden when the parent type is still a fresh draft
 * (no slug yet) — variants only make sense once the type has been at least saved once.
 */
export function LayoutVariantManager({ slug, fieldNames }: LayoutVariantManagerProps): JSX.Element | null {
  const pushToast = useToastStore((s) => s.push);
  const { data: variants, isLoading, isError, error } = useLayoutVariants(slug ?? undefined);
  const addMutation = useAddLayoutVariant();
  const removeMutation = useRemoveLayoutVariant();

  const [showForm, setShowForm] = useState(false);

  if (slug === null) {
    return null;
  }

  const handleRemove = (variant: LayoutVariantResponse): void => {
    removeMutation.mutate(
      { slug, variantId: variant.id },
      {
        onSuccess: () => pushToast({ tone: 'success', title: `Variante "${variant.name}" eliminada.` }),
        onError: (err) => {
          const description = err instanceof Error ? err.message : 'No se pudo eliminar la variante.';
          pushToast({ tone: 'error', title: 'Error al eliminar variante', description });
        },
      },
    );
  };

  return (
    <section aria-labelledby="variants-heading" className="card space-y-4 p-4" data-testid="layout-variant-manager">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="variants-heading" className="text-base font-semibold text-slate-900">
            Variantes de layout
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Reconoce diferentes disposiciones (p.ej. plantillas de distintos proveedores) sin duplicar el schema.
            El motor las usa como pistas para guiar la extracción cuando una muestra coincide.
          </p>
        </div>
        {!showForm ? (
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() => setShowForm(true)}
            data-testid="add-variant-button"
          >
            + Añadir variante
          </button>
        ) : null}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <LoadingSpinner />
        </div>
      ) : isError ? (
        <ErrorBanner error={error} />
      ) : (variants ?? []).length === 0 ? (
        <p className="text-sm text-slate-500">
          Este tipo no tiene variantes registradas todavía. La extracción usa el comportamiento por defecto.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {(variants ?? []).map((variant) => (
            <VariantRow
              key={variant.id}
              variant={variant}
              busy={removeMutation.isPending}
              onRemove={() => handleRemove(variant)}
            />
          ))}
        </ul>
      )}

      {showForm ? (
        <AddVariantForm
          slug={slug}
          fieldNames={fieldNames}
          busy={addMutation.isPending}
          onCancel={() => setShowForm(false)}
          onSubmit={(body) => {
            addMutation.mutate(
              { slug, body },
              {
                onSuccess: (variant) => {
                  pushToast({ tone: 'success', title: `Variante "${variant.name}" añadida.` });
                  setShowForm(false);
                },
                onError: (err) => {
                  const description = err instanceof Error ? err.message : 'No se pudo añadir la variante.';
                  pushToast({ tone: 'error', title: 'Error al añadir variante', description });
                },
              },
            );
          }}
        />
      ) : null}
    </section>
  );
}

interface VariantRowProps {
  variant: LayoutVariantResponse;
  busy: boolean;
  onRemove: () => void;
}

function VariantRow({ variant, busy, onRemove }: VariantRowProps): JSX.Element {
  return (
    <li className="flex items-start justify-between gap-3 py-3" data-testid={`variant-row-${variant.id}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-900">{variant.name}</p>
        <p className="mt-0.5 text-xs text-slate-500">
          {Object.keys(variant.fieldRegions).length} regiones ·{' '}
          <span className="font-mono">{variant.signature.slice(0, 12)}…</span>
        </p>
      </div>
      <button
        type="button"
        className="text-xs text-rose-600 hover:underline disabled:opacity-50"
        disabled={busy}
        onClick={onRemove}
      >
        Eliminar
      </button>
    </li>
  );
}

interface AddVariantFormProps {
  slug: string;
  fieldNames: ReadonlyArray<string>;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: { name: string; exemplarJobId: string; fieldRegions: Record<string, DocumentRegionName> }) => void;
}

function AddVariantForm({ fieldNames, busy, onCancel, onSubmit }: AddVariantFormProps): JSX.Element {
  const [name, setName] = useState('');
  const [exemplarJobId, setExemplarJobId] = useState('');
  const [regions, setRegions] = useState<Record<string, DocumentRegionName>>({});

  const setRegion = (fieldName: string, region: DocumentRegionName | ''): void => {
    setRegions((prev) => {
      const next = { ...prev };
      if (region === '') {
        delete next[fieldName];
      } else {
        next[fieldName] = region;
      }
      return next;
    });
  };

  const submit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (Object.keys(regions).length === 0) {
      return;
    }
    onSubmit({ name, exemplarJobId, fieldRegions: regions });
  };

  return (
    <form onSubmit={submit} className="space-y-3 border-t border-slate-200 pt-4" data-testid="add-variant-form">
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Nombre *</span>
        <input
          type="text"
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
      </label>

      <label className="block text-sm">
        <span className="font-medium text-slate-700">Job ejemplar *</span>
        <input
          type="text"
          required
          value={exemplarJobId}
          onChange={(e) => setExemplarJobId(e.target.value)}
          placeholder="job_abc12345"
          className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm focus:border-primary focus:outline-none"
        />
        <span className="mt-1 block text-xs text-slate-500">
          Id de un documento ya procesado con OCR completo de este mismo tipo. El sistema calcula la firma
          desde su OCR cacheado sin reanalizar el PDF.
        </span>
      </label>

      <fieldset className="space-y-2 rounded border border-slate-200 p-3">
        <legend className="px-1 text-xs font-medium uppercase tracking-wide text-slate-500">
          Regiones por campo (al menos una)
        </legend>
        {fieldNames.length === 0 ? (
          <p className="text-xs text-slate-500">
            El tipo no tiene campos declarados. Añade campos al schema antes de crear una variante.
          </p>
        ) : (
          fieldNames.map((fieldName) => (
            <div key={fieldName} className="flex items-center gap-2 text-sm">
              <label className="w-48 truncate text-slate-700" htmlFor={`region-${fieldName}`}>
                {fieldName}
              </label>
              <select
                id={`region-${fieldName}`}
                value={regions[fieldName] ?? ''}
                onChange={(e) => setRegion(fieldName, e.target.value as DocumentRegionName | '')}
                className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm focus:border-primary focus:outline-none"
              >
                <option value="">— sin pista —</option>
                {REGION_OPTIONS.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>
          ))
        )}
      </fieldset>

      <div className="flex items-center justify-end gap-3">
        <button type="button" className="text-sm text-slate-500 hover:underline" onClick={onCancel}>
          Cancelar
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={busy || Object.keys(regions).length === 0 || name === '' || exemplarJobId === ''}
        >
          {busy ? 'Guardando…' : 'Crear variante'}
        </button>
      </div>
    </form>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const message = error instanceof ApiError ? error.message : 'No se pudieron cargar las variantes.';
  return (
    <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
      {message}
    </div>
  );
}
